import {
  FanguardApiContractsClientEmailTemplateCreateRequestModel,
  FanguardApiContractsClientEmailTemplateModel,
  FanguardApiContractsClientSendTestEmailRequestModel,
} from 'src/api/generated';

import { get, patch, post, remove } from '@/api/api';
import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';
import { App } from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutationWithMessages } from './helpers';
import { AxiosResponse } from 'axios';

const URI = 'email-templates';

const getEmailTemplatesOptions = () => ({
  queryKey: [URI],
  queryFn: async () => {
    const response = await get<FanguardApiContractsClientEmailTemplateModel[]>({ uri: URI });
    return response.data;
  },
});

const useGetEmailTemplates = (): UseQueryResult<FanguardApiContractsClientEmailTemplateModel[], Error> => {
  return useQuery(getEmailTemplatesOptions());
};

const useGetEmailTemplate = (id: string): UseQueryResult<FanguardApiContractsClientEmailTemplateModel, Error> => {
  return useQuery({
    queryKey: [URI, id],
    queryFn: async () => {
      const response = await get<FanguardApiContractsClientEmailTemplateModel>({ uri: `${URI}/${id}` });
      return response.data;
    },
    enabled: !!id,
  });
};

interface SendTestEmailPayload extends FanguardApiContractsClientSendTestEmailRequestModel {
  id: string;
}

const useSendTestEmail = () => {
  const { message } = App.useApp();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (data: SendTestEmailPayload) => {
      const response = await post({ uri: `${URI}/${data.id}/send-test`, data });
      return response.data;
    },
    onSuccess: () => {
      message.success(t('emailTemplates.newEditEmailTemplate.sentTestEmailSuccess'));
    },
    onError: () => {
      message.error(t('emailTemplates.newEditEmailTemplate.sentTestEmailError'));
    },
  });
};

const useCreateEmailTemplate = () => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientEmailTemplateModel>,
    Error,
    FanguardApiContractsClientEmailTemplateCreateRequestModel
  >((data) => post({ uri: URI, data }), {
    action: 'create',
    entityType: 'emailTemplate',
    queryKeysToInvalidate: [URI],
  });
};

const useUpdateEmailTemplate = (id: string) => {
  return useMutationWithMessages<
    AxiosResponse<FanguardApiContractsClientEmailTemplateModel>,
    Error,
    FanguardApiContractsClientEmailTemplateModel
  >((data) => patch({ uri: `${URI}/${data.id}`, data }), {
    action: 'update',
    entityType: 'emailTemplate',
    queryKeysToInvalidate: [URI, id],
  });
};

const useDeleteEmailTemplate = () => {
  return useMutationWithMessages((id: string) => remove({ uri: `${URI}/${id}` }), {
    action: 'delete',
    entityType: 'emailTemplate',
    queryKeysToInvalidate: [URI],
  });
};

export {
  useGetEmailTemplates,
  useSendTestEmail,
  useGetEmailTemplate,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
};
